// Pure client-side navigation rule evaluator for live survey taking.
// Mirrors assessment_builder/services/navigation_evaluator.py.
//
// Extracted from SurveyTaking.jsx so the logic can be tested in isolation.
// Any divergence between this file and the Python evaluator is a bug — keep
// them in sync.

const DECIMAL_NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

const normalizeVal = (v) => {
  if (v === null || v === undefined) return v;
  if (typeof v === 'number') return v;
  // Python bool is numeric; preserve API equality semantics (true == 1).
  if (typeof v === 'boolean') return v ? 1 : 0;
  const text = String(v).trim();
  // Require a complete decimal representation. parseFloat('3 points')
  // returning 3 made the browser disagree with the Python evaluator.
  if (text !== '' && DECIMAL_NUMBER.test(text)) {
    const n = Number(text);
    if (Number.isFinite(n)) return n;
  }
  return text.toLowerCase();
};

const compare = (left, op, right) => {
  try {
    const l = normalizeVal(left);
    const r = normalizeVal(right);
    const relationalCompare = (predicate) => (
      typeof l === 'number'
      && Number.isFinite(l)
      && typeof r === 'number'
      && Number.isFinite(r)
      && predicate(l, r)
    );
    switch (op) {
      case '==': return l === r;
      case '!=': return l !== r;
      case '>':  return relationalCompare((a, b) => a > b);
      case '<':  return relationalCompare((a, b) => a < b);
      case '>=': return relationalCompare((a, b) => a >= b);
      case '<=': return relationalCompare((a, b) => a <= b);
      case 'in': return Array.isArray(right) ? right.map(normalizeVal).includes(l) : false;
      case 'not_in': return Array.isArray(right) ? !right.map(normalizeVal).includes(l) : true;
      default: return false;
    }
  } catch {
    return false;
  }
};

const buildResponseMap = (currentResponses, screenHistory) => {
  const allResponses = {};
  (screenHistory || []).forEach((h) => Object.assign(allResponses, h.existing_responses || {}));
  Object.assign(allResponses, currentResponses || {});
  return allResponses;
};

const makeGetResponse = (allResponses) => (questionId) => {
  const str = String(questionId);
  if (str in allResponses) return allResponses[str];
  const num = /^\d+$/.test(str) ? Number(str) : NaN;
  if (Number.isFinite(num) && num in allResponses) return allResponses[num];
  return allResponses[questionId] ?? null;
};

const evalCondition = (rule, ruleScreen, getResponse) => {
  const condType = rule.condition_type || rule.condition?.type || 'answer_value';
  const op = rule.operator || rule.condition?.operator || '==';
  const val = rule.value !== undefined ? rule.value : rule.condition?.value;

  if (condType === 'answer_value') {
    const qids = rule.backend_question_ids || rule.question_ids || [];
    const qid = qids[0] ?? rule.condition?.question_id ?? 'current';
    const resolvedId = qid === 'current'
      // Python resolves "current" against the question the rule is attached
      // to, not whichever screen happens to be displayed during the
      // survey-wide walk.
      ? String(ruleScreen?.elements?.find((e) => e.type === 'question')?.questionId ?? '')
      : String(qid);
    const resp = getResponse(resolvedId);
    return resp !== null && resp !== undefined ? compare(resp, op, val) : false;
  }

  if (condType === 'cumulative_score') {
    const qids = rule.backend_question_ids || rule.question_ids || rule.condition?.question_ids || [];
    const values = qids.map((id) => {
      const r = getResponse(String(id));
      if (r === null || r === undefined) return null;
      const numeric = Number(r);
      return Number.isFinite(numeric) ? numeric : 0;
    });
    if (values.some((v) => v === null)) return false;
    return compare(values.reduce((a, b) => a + b, 0), op, val);
  }

  if (condType === 'answer_combination') {
    const logic = rule.logic || rule.condition?.logic || 'and';
    const subs = rule.backend_conditions || rule.conditions || rule.condition?.conditions || [];
    if (!subs.length) return false;
    // Both AND and OR combinations defer until every referenced response is
    // present.  This is the documented API behavior and prevents an OR rule
    // from firing prematurely while a later contributing question is unseen.
    if (subs.some((sub) => {
      const value = getResponse(String(sub.question_id));
      return value === null || value === undefined;
    })) return false;
    const results = subs.map((sub) => {
      const r = getResponse(String(sub.question_id));
      return compare(r, sub.operator, sub.value);
    });
    return logic === 'or' ? results.some(Boolean) : results.every(Boolean);
  }

  return false;
};

const resultForRule = (rule, log) => {
  const actionType = rule.action_type || rule.action?.type || 'continue';
  const ruleId = rule.rule_id || rule.id || null;
  // rule_id is only added when present — keeps the return shape
  // backward-compatible for existing consumers and tests.
  const withRuleId = (base) => (ruleId == null ? base : { ...base, rule_id: String(ruleId) });
  if (actionType === 'skip_to') {
    const result = withRuleId({
      action: 'skip_to',
      next_screen_id: String(rule.target_screen_id || rule.action?.target_screen_id || ''),
    });
    // Pass through name/index so consumers can rebind when target_screen_id
    // is stale (e.g. assessment cloned/republished and screens got new IDs).
    const targetName = rule.target_screen_name ?? rule.action?.target_screen_name;
    if (targetName) result.target_screen_name = String(targetName);
    const rawIndex = rule.target_screen_index ?? rule.action?.target_screen_index;
    const idx = Number(rawIndex);
    if (rawIndex !== undefined && rawIndex !== null && Number.isInteger(idx)) {
      result.target_screen_index = idx;
    }
    log('  → returning', result);
    return result;
  }
  if (actionType === 'end_survey') {
    log('  → returning end_survey');
    return withRuleId({ action: 'end_survey' });
  }
  if (actionType === 'show_alert') {
    const result = withRuleId({
      action: 'show_alert',
      alert_message: rule.alert_message || rule.action?.alert_message || '',
    });
    log('  → returning', result);
    return result;
  }
  return null;
};

export const evaluateNavigationRules = ({
  screen,
  responses = {},
  screenHistory = [],
  allScreens = null,
  firedRuleIds = [],
  debug = false,
}) => {
  const log = debug ? (...args) => console.log('[navRules]', ...args) : () => {};

  const allResponses = buildResponseMap(responses, screenHistory);
  const getResponse = makeGetResponse(allResponses);

  // FECOA-1396: walk every screen's rules in deterministic order (matching
  // the backend `evaluate_navigation_across_survey`) when `allScreens` is
  // provided. A rule attached to screen M referencing answers on screen N
  // (M != N) would otherwise be invisible — the prior implementation only
  // iterated the current screen's `navigationRules`, so cross-screen
  // cumulative / combination rules silently fell through to the next
  // sequential screen.
  const screensToWalk = Array.isArray(allScreens) && allScreens.length > 0
    ? allScreens
    : (screen ? [screen] : []);

  if (screensToWalk.length === 0) {
    log('no screen provided');
    return { action: 'continue' };
  }

  const fired = new Set((firedRuleIds || []).map(String));
  const currentScreenId = screen?.screen_id != null ? String(screen.screen_id) : null;

  log('evaluating', {
    current_screen_id: currentScreenId,
    screens_walked: screensToWalk.length,
    mergedResponses: allResponses,
  });

  for (const s of screensToWalk) {
    const rulesByQuestion = s?.navigationRules;
    if (!rulesByQuestion) continue;
    for (const [qid, rules] of Object.entries(rulesByQuestion)) {
      const skipRules = rules.skip_rules || [];
      log(`screen ${s.screen_id} question ${qid}: ${skipRules.length} skip_rule(s)`);
      for (const rule of skipRules) {
        if (rule.enabled === false) {
          log('  rule skipped — disabled', rule);
          continue;
        }
        const ruleId = rule.rule_id || rule.id;
        if (ruleId != null && fired.has(String(ruleId))) {
          log('  rule skipped — already fired this session', ruleId);
          continue;
        }
        const matched = evalCondition(rule, s, getResponse);
        log(`  rule ${ruleId || '(no id)'} matched=${matched}`, rule);
        if (!matched) continue;
        const result = resultForRule(rule, log);
        if (!result) continue;
        // Self-targeting guard: a skip_to whose target is the current screen
        // would pin the participant in place. Fall through to next rule.
        if (
          result.action === 'skip_to'
          && currentScreenId != null
          && result.next_screen_id === currentScreenId
        ) {
          log('  rule self-targets current screen, skipping');
          continue;
        }
        return result;
      }
    }
  }

  log('no rule matched — continue');
  return { action: 'continue' };
};

export const __internals = { normalizeVal, compare, buildResponseMap, makeGetResponse, evalCondition };
